import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { StatusBar, BottomNav, Logo } from "../components/Shell";
import Ico from "../components/Ico";
import { useAppStore } from "../components/AppStore";
import SosDialer from "../components/SosDialer";
import SafeArrivalCard from "../components/SafeArrivalCard";
import { currentUser, rankTiers, chores, quickActions, badges } from "../mock";
import { Bell, Star, DollarSign, Flame, ArrowRight, MapPin, Clock, PhoneCall } from "lucide-react";

export default function Home() {
  const navigate = useNavigate();
  const { statusOf, rankIndex, jobsDone, earnedTotal, emergency } = useAppStore();
  const [sosOpen, setSosOpen] = useState(false);
  const rank = rankTiers[rankIndex];
  const nextRank = rankTiers[rankIndex + 1];
  const nextName = nextRank ? nextRank.title[0] + nextRank.title.slice(1).toLowerCase() : "CEO";
  const pct = nextRank ? Math.min(100, Math.round((jobsDone / nextRank.jobs) * 100)) : 100;
  const goalPct = Math.round((currentUser.weeklyEarned / currentUser.weeklyGoal) * 100);
  const hot = chores.slice(0, 4);

  return (
    <div className="gd-screen bg-[#eef1fb]">
      <div className="gd-header-grad flex-shrink-0 pb-16 pt-0" style={{ borderBottomLeftRadius: 30, borderBottomRightRadius: 30 }}>
        <StatusBar dark={false} />
        <div className="flex items-center justify-between px-5 pt-1">
          <Logo size="sm" />
          <div className="flex items-center gap-2.5">
            <button onClick={() => setSosOpen(true)} className="gd-tap gd-sos-pulse flex items-center gap-1 rounded-full bg-[#e0533d] px-3 py-2 text-white" style={{ boxShadow: "0 8px 18px rgba(224,83,61,.4)" }}>
              <PhoneCall size={15} /><span className="text-xs font-extrabold tracking-wide">SOS</span>
            </button>
            <button onClick={() => navigate("/notifications")} className="gd-tap relative grid h-10 w-10 place-items-center rounded-full bg-white/15">
              <Bell size={18} color="#fff" />
              <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-[#f5911e]" />
            </button>
            <button onClick={() => navigate("/profile")} className="gd-tap grid h-10 w-10 place-items-center rounded-full bg-[#f5911e] font-extrabold text-white">
              {currentUser.initial}
            </button>
          </div>
        </div>
      </div>

      <div className="gd-scroll -mt-12 px-4 pb-6 gd-stagger">
        {/* rank / level card */}
        <button onClick={() => navigate("/ranks")} className="gd-tap block w-full text-left">
        <div className="gd-card p-4">
          <div className="flex items-center gap-4">
            <div className="relative grid h-16 w-16 place-items-center">
              <svg className="absolute" width="64" height="64" viewBox="0 0 64 64">
                <circle cx="32" cy="32" r="27" fill="none" stroke="#e9edfb" strokeWidth="6" />
                <circle cx="32" cy="32" r="27" fill="none" stroke="#f5911e" strokeWidth="6" strokeLinecap="round"
                  strokeDasharray={2 * Math.PI * 27} strokeDashoffset={2 * Math.PI * 27 * (1 - pct / 100)} transform="rotate(-90 32 32)" />
              </svg>
              <div className="grid h-11 w-11 place-items-center rounded-full" style={{ background: rank.color }}>
                <Ico name={rank.icon} size={20} color="#fff" />
              </div>
            </div>
            <div className="flex-1">
              <p className="text-xs font-semibold text-[#8b93b5]">Good morning, {currentUser.name}</p>
              <div className="mt-0.5 flex items-center gap-1.5">
                <span className="text-[15px] font-extrabold text-[#1f2a52]">{rank.title[0] + rank.title.slice(1).toLowerCase()}</span>
                <span className="rounded-full px-2 py-0.5 text-[9px] font-bold text-white" style={{ background: rank.color }}>{rank.tier}</span>
              </div>
              <div className="mt-2 h-2 rounded-full bg-[#e9edfb]">
                <div className="h-2 rounded-full" style={{ width: `${pct}%`, background: "linear-gradient(90deg,#f5a63a,#f5911e)" }} />
              </div>
              <p className="mt-1 text-[10px] font-semibold text-[#9aa3c0]">{jobsDone} jobs · ${earnedTotal} · {nextRank ? `${Math.max(0, nextRank.jobs - jobsDone)} jobs to ${nextName}` : "Top rank!"}</p>
            </div>
          </div>
        </div>
        </button>

        {/* stat pills */}
        <div className="mt-3 grid grid-cols-3 gap-3">
          <Stat icon={DollarSign} tint="#4fae3f" value={`$${currentUser.weeklyEarned}`} label="This Week" />
          <Stat icon={Flame} tint="#f5911e" value={`${currentUser.streak}d`} label="Streak" />
          <Stat icon={Star} tint="#e6b325" value={currentUser.rating} label="Rating" />
        </div>

        {/* weekly goal */}
        <div className="mt-3 overflow-hidden rounded-[22px] p-4 text-white" style={{ background: "linear-gradient(135deg,#57b24a,#3d8f34)" }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-white/80">Weekly Goal</p>
              <p className="text-lg font-extrabold">Earn ${currentUser.weeklyGoal} by Sunday</p>
            </div>
            <button onClick={() => navigate("/explore")} className="gd-tap flex items-center gap-1 rounded-full bg-white px-3 py-2 text-xs font-extrabold text-[#3d8f34]">
              Find Tasks <ArrowRight size={13} />
            </button>
          </div>
          <div className="mt-3 h-2.5 rounded-full bg-white/25">
            <div className="h-2.5 rounded-full bg-white" style={{ width: `${goalPct}%` }} />
          </div>
          <p className="mt-1.5 text-[11px] font-semibold text-white/85">${currentUser.weeklyEarned} earned · ${currentUser.weeklyGoal - currentUser.weeklyEarned} remaining</p>
        </div>

        {/* safe arrival check */}
        <div className="mt-3">
          <SafeArrivalCard />
        </div>

        {/* quick actions */}
        <h3 className="mb-2 mt-5 text-base font-extrabold text-[#1f2a52]">Quick Actions</h3>
        <div className="grid grid-cols-4 gap-2">
          {quickActions.map((q) => (
            <button key={q.label} onClick={() => navigate(q.to)} className="gd-tap flex flex-col items-center gap-1.5">
              <div className="grid h-14 w-14 place-items-center rounded-2xl" style={{ background: `${q.tint}18` }}>
                <Ico name={q.icon} size={22} color={q.tint} />
              </div>
              <span className="text-center text-[10px] font-bold leading-tight text-[#5b648a]">{q.label}</span>
            </button>
          ))}
        </div>

        {/* hot chores */}
        <div className="mb-2 mt-5 flex items-center justify-between">
          <h3 className="text-base font-extrabold text-[#1f2a52]">Hot Chores Near You</h3>
          <button onClick={() => navigate("/explore")} className="text-xs font-bold text-[#3b57c9]">See All →</button>
        </div>
        <div className="space-y-3">
          {hot.map((c) => (
            <button key={c.id} onClick={() => navigate(`/task/${c.id}`)} className="gd-tap flex w-full items-center gap-3 rounded-[20px] bg-white p-3 text-left shadow-[0_8px_24px_rgba(31,42,82,0.06)]">
              <div className="grid h-14 w-14 flex-shrink-0 place-items-center rounded-2xl" style={{ background: `${c.tint}18` }}>
                <Ico name={c.icon} size={24} color={c.tint} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-extrabold text-[#1f2a52]">{c.title}</span>
                  {c.urgent && <span className="rounded-full bg-[#fdece0] px-1.5 py-0.5 text-[8px] font-extrabold text-[#e97f0c]">URGENT</span>}
                </div>
                <div className="mt-1 flex items-center gap-1 text-[11px] text-[#f5b301]">
                  {Array.from({ length: 5 }).map((_, i) => <Star key={i} size={11} fill={i < Math.round(c.rating) ? "#f5b301" : "none"} color="#f5b301" />)}
                </div>
                <div className="mt-1 flex items-center gap-3 text-[10px] font-semibold text-[#9aa3c0]">
                  <span className="flex items-center gap-0.5"><Clock size={10} /> {c.duration}</span>
                  <span className="flex items-center gap-0.5"><MapPin size={10} /> {c.distance}</span>
                </div>
              </div>
              <div className="flex-shrink-0 text-right">
                <p className="text-lg font-black text-[#f5911e]">${c.pay}</p>
                <p className="text-[9px] font-bold text-[#8b5cf6]">+{c.xp} XP</p>
                {statusOf(c.id) && <span className="mt-1 inline-block rounded-full bg-[#eaf6ea] px-1.5 py-0.5 text-[8px] font-bold text-[#3d8f34]">{statusOf(c.id) === "approved" ? "Approved" : "Pending"}</span>}
              </div>
            </button>
          ))}
        </div>

        {/* badges */}
        <div className="mb-2 mt-5 flex items-center justify-between">
          <h3 className="text-base font-extrabold text-[#1f2a52]">Recent Badges</h3>
          <button onClick={() => navigate("/profile")} className="text-xs font-bold text-[#3b57c9]">View All →</button>
        </div>
        <div className="grid grid-cols-4 gap-2 pb-2">
          {badges.map((b) => (
            <div key={b.name} className="flex flex-col items-center gap-1.5 rounded-2xl bg-white p-3 shadow-[0_6px_18px_rgba(31,42,82,0.05)]">
              <div className="grid h-11 w-11 place-items-center rounded-full" style={{ background: `${b.tint}18` }}>
                <Ico name={b.icon} size={19} color={b.tint} />
              </div>
              <span className="text-center text-[9px] font-bold leading-tight text-[#5b648a]">{b.name}</span>
            </div>
          ))}
        </div>
      </div>

      <BottomNav />
      <SosDialer open={sosOpen} onClose={() => setSosOpen(false)} />
    </div>
  );
}

function Stat({ icon: Icon, tint, value, label }) {
  return (
    <div className="flex flex-col items-center rounded-[18px] bg-white py-3 shadow-[0_8px_20px_rgba(31,42,82,0.05)]">
      <Icon size={18} color={tint} />
      <span className="mt-1 text-lg font-black text-[#1f2a52]">{value}</span>
      <span className="text-[10px] font-semibold text-[#9aa3c0]">{label}</span>
    </div>
  );
}
